import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { BookingServices } from '@/api/services/booking';
import { platformService } from '@/api/services/platform';
import { calculatorSearchSchema } from '@/components/Calculator/utils';
import { CLOUD_PROVIDER_MAP } from '@/components/Clusters/constants';
import { selectDefaultRegion } from '@/components/Clusters/helpers';
import { head } from '@/router/Head';
import { resolvePreloadedProvidersRegions } from '@/router/resolvers/cluster';

export const Route = createFileRoute('/(public)/calculator')({
  validateSearch: calculatorSearchSchema,
  staticData: {
    title: 'Calculator',
    domain: 'billing',
  },
  loaderDeps: ({ search: { region, provider } }) => ({ region, provider }),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const { provider, region } = search;

    const listGlobalCloudProviderRegionsQuery = platformService.listGlobalCloudProviderRegionsQuery({
      cloudProviderId: provider,
    });
    // Not hybrid cloud provider, fetch the regions for the cloud provider id
    const { items: cloudProviderRegions } = await queryClient.fetchQuery(listGlobalCloudProviderRegionsQuery);
    const defaultRegion = selectDefaultRegion({
      cloudProviderRegions: cloudProviderRegions.filter(({ available }) => available),
      preSelectedRegion: region,
    });
    // If the region is not available for the selected cloud provider, we need to redirect to the default region
    if (defaultRegion !== region) {
      throw redirect({
        to: '/calculator',
        search: { ...search, region: defaultRegion },
        replace: true,
      });
    }

    return {
      listGlobalCloudProvidersQuery: platformService.listGlobalCloudProvidersQuery(),
      listGlobalCloudProviderRegionsQuery,
      bookingAvailablePackagesQueryOptions: BookingServices.listGlobalPackagesQuery({
        cloudProviderId: provider,
        cloudProviderRegionId: region,
      }),
    };
  },
  loader: async ({ context: { queryClient, bookingAvailablePackagesQueryOptions } }) => {
    void queryClient.prefetchQuery(bookingAvailablePackagesQueryOptions);

    await resolvePreloadedProvidersRegions({
      queryClient,
      filter: ({ id, available }) => available && id !== CLOUD_PROVIDER_MAP.HYBRID,
    });
  },
  component: lazyRouteComponent(() => import('@/components/Calculator/CalculatorPage'), 'CalculatorComponent'),
  head,
});
